/**
 * Partenaires — l'espace sponsoring.
 *
 * Les solutions Elite Lead d'abord (c'est l'éditeur qui offre l'application),
 * puis les partenaires. Chaque encart passe par une redirection comptée : un
 * sponsor achète de l'attention mesurée, pas une place sur une page.
 */
import { useEffect, useState } from 'react';
import { ArrowUpRight } from 'lucide-react';
import { appelerApi } from '../../reseau.ts';
import { Card, PageTitle, SectionLabel } from '../../admin/ui.tsx';
import type { Partenaire } from './types.ts';

export function Partenaires() {
  const [partenaires, setPartenaires] = useState<Partenaire[] | null>(null);

  useEffect(() => {
    void appelerApi<{ partenaires: Partenaire[] }>('/api/partenaires').then((r) =>
      setPartenaires(r.donnees?.partenaires ?? []),
    );
  }, []);

  const categories = [...new Set((partenaires ?? []).map((p) => p.categorie ?? 'Partenaire'))];

  return (
    <>
      <PageTitle
        title={
          <>
            Nos <em className="text-match-gradient font-serif italic font-medium">partenaires</em>
          </>
        }
        subtitle="EventMatch.pro vous est offert par Elite Lead. Voici ce que nous faisons le reste du temps, et les partenaires qui font tourner l’écosystème avec nous."
      />

      {partenaires === null && <p className="text-[14px] text-fumee">Chargement…</p>}
      {partenaires?.length === 0 && <p className="text-[14px] text-fumee">Aucun partenaire pour l’instant.</p>}

      {categories.map((categorie) => (
        <section key={categorie} className="mb-8">
          <SectionLabel>{categorie}</SectionLabel>
          <div className="mt-3 grid gap-4 lg:grid-cols-2">
            {partenaires
              ?.filter((p) => (p.categorie ?? 'Partenaire') === categorie)
              .map((p) => (
                <a
                  key={p.id}
                  href={`/api/partenaires/${p.id}/aller`}
                  target="_blank"
                  rel="noreferrer"
                  className="group block"
                >
                  <Card className="flex h-full flex-col transition-colors duration-200 group-hover:bg-[#262626]">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-[16px] text-blanc">{p.nom}</p>
                        <p className="mt-1 text-[14px] leading-relaxed text-fumee">{p.accroche}</p>
                      </div>
                      {p.logo_url ? (
                        <img src={p.logo_url} alt="" className="h-10 w-10 shrink-0 rounded-[6px] object-contain" />
                      ) : (
                        <ArrowUpRight size={16} strokeWidth={1.5} className="shrink-0 text-fumee group-hover:text-blanc" />
                      )}
                    </div>
                    {p.description && <p className="mt-3 flex-1 text-[13px] leading-relaxed text-fumee">{p.description}</p>}
                    <span className="mt-4 inline-flex items-center gap-1 text-[13px] text-blanc">
                      En savoir plus <ArrowUpRight size={13} strokeWidth={1.5} />
                    </span>
                  </Card>
                </a>
              ))}
          </div>
        </section>
      ))}

      <Card>
        <SectionLabel>Devenir partenaire</SectionLabel>
        <p className="mt-2 max-w-[640px] text-[14px] leading-relaxed text-fumee">
          Votre solution a sa place ici ? Les encarts partenaires sont ouverts aux entreprises de
          l’écosystème. Écrivez à{' '}
          <a href="mailto:bonjour@elitelead.be?subject=Devenir%20partenaire%20EventMatch.pro" className="text-blanc underline">
            bonjour@elitelead.be
          </a>
          .
        </p>
      </Card>
    </>
  );
}
